// Centralised domain-status → Badge variant + label mapper. Single source of
// truth for how tracking statuses surface visually across the tracking
// tabs. For `kind="rag"`, emits the colour-dot + sr-only pattern. The
// milestone branch consumes `MilestoneDisplayStatus`; purpose-built variants
// (`upcoming`, `dueSoon`, `partiallyCompleted`, `overdue`, `completedLate`)
// keep the milestone vocabulary semantically distinct from the
// insurance/deliverable lifecycle.
// Meeting status pills are handled by the dedicated MeetingStatusPill
// primitive (5-state vocabulary lives in brand SKILL.md §"Meeting status pills").
// SOW status pills (4 states: draft / active / completed / cancelled) ride
// the existing `.badge-*` classes — see brand SKILL.md §9.1.
// Variation status pills (3 states: proposed / approved / rejected) ride
// the same `.badge-*` classes — proposed=amber, approved=emerald,
// rejected=red. See brand SKILL.md §9.2.

import { clsx } from "clsx";
import {
  RAG_LABELS,
  type DeliverableDerivedStatus,
  type InsuranceStatus,
  type IssueStatus,
  type MilestoneDisplayStatus,
  type MilestoneDisplayStatusV2,
  type MilestoneRiskOverlayKind,
  type RagRating,
  type SowStatus,
  type VariationStatus,
} from "../../../../types";
import type { MilestoneDerivedStatus } from "../../../../utils/deriveMilestoneStatus";

type BadgeVariant =
  | "draft"
  | "active"
  | "pending"
  | "expired"
  | "completed"
  | "archived"
  | "expiredHistorical"
  | "upcoming"
  | "dueSoon"
  | "expiring"
  | "delivered"
  | "partiallyCompleted"
  | "overdue"
  | "completedLate"
  | "inReview"
  | "late"
  | "outlinedAmber"
  | "outlinedRed"
  | "outlinedRose"
  | "filledRed"
  | "openIssue"
  | "outlinedSky"
  | "outlinedEmerald"
  | "closed";

// Explicit variant → class map. The literal strings are what Tailwind's
// content scanner reads — runtime string concatenation of "badge-" + variant
// hides the class names from the scanner and the .badge-* component styles
// get tree-shaken from the build. `Record<BadgeVariant, …>` makes a missing
// entry a compile error, so the map cannot drift from the variant union.
// This map is the canonical full set of badge classes: the other dynamic
// badge sites (common/Badge, LifecycleTransitionDialog, ContractDetailsTab)
// all draw from subsets of this union, so these literals keep their classes
// in the emitted CSS too.
const BADGE_CLASS: Record<BadgeVariant, string> = {
  draft: "badge-draft",
  active: "badge-active",
  pending: "badge-pending",
  expired: "badge-expired",
  completed: "badge-completed",
  archived: "badge-archived",
  expiredHistorical: "badge-expiredHistorical",
  upcoming: "badge-upcoming",
  dueSoon: "badge-dueSoon",
  expiring: "badge-expiring",
  delivered: "badge-delivered",
  partiallyCompleted: "badge-partiallyCompleted",
  overdue: "badge-overdue",
  completedLate: "badge-completedLate",
  inReview: "badge-inReview",
  late: "badge-late",
  outlinedAmber: "badge-outlinedAmber",
  outlinedRed: "badge-outlinedRed",
  outlinedRose: "badge-outlinedRose",
  filledRed: "badge-filledRed",
  openIssue: "badge-openIssue",
  outlinedSky: "badge-outlinedSky",
  outlinedEmerald: "badge-outlinedEmerald",
  closed: "badge-closed",
};

interface InsuranceBadgeProps {
  kind: "insurance";
  status: InsuranceStatus;
  /** True when the record has a qualifying active successor (it has been
   *  renewed). On an `expired` row this renders the muted slate "Expired"
   *  badge so audit-only data does not compete with active expired records;
   *  on an `expiring` row it renders the "Renewed" pill instead of the amber
   *  "Expiring soon" pill (and the row no longer raises the attention dot). */
  superseded?: boolean;
  /** Name of the successor record. When set on a superseded `expiring` row it
   *  is surfaced as the Renewed pill's `title` so the suppressed attention dot
   *  is auditable on hover. */
  successorName?: string;
}
interface DeliverableBadgeProps {
  kind: "deliverable";
  status: DeliverableDerivedStatus;
}
interface MilestoneBadgeProps {
  kind: "milestone";
  status: MilestoneDisplayStatus | MilestoneDerivedStatus | MilestoneDisplayStatusV2;
}
interface RagBadgeProps {
  kind: "rag";
  rating: RagRating;
}
interface SowBadgeProps {
  kind: "sow";
  status: SowStatus;
}
interface VariationBadgeProps {
  kind: "variation";
  status: VariationStatus;
}
interface IssueBadgeProps {
  kind: "issue";
  status: IssueStatus;
}

export type TrackingStatusBadgeProps =
  | InsuranceBadgeProps
  | DeliverableBadgeProps
  | MilestoneBadgeProps
  | RagBadgeProps
  | SowBadgeProps
  | VariationBadgeProps
  | IssueBadgeProps;

const INSURANCE_MAP: Record<
  InsuranceStatus,
  { variant: BadgeVariant; label: string }
> = {
  upcoming: { variant: "draft", label: "Future" },
  current: { variant: "active", label: "Current" },
  // Amber (not the shared brand-violet dueSoon): insurance reads as a
  // current → expiring → expired traffic light, so the middle state sits
  // beside the red "Expired" pill. Milestone/meeting "Due soon" stays violet.
  expiring: { variant: "expiring", label: "Expiring soon" },
  expired: { variant: "expired", label: "Expired" },
};

// Deliverable pill palette (outlined-vs-filled convention):
//   not-started        → slate (draft)              — neutral pre-work
//   submitted          → outlined amber             — customer decision required
//   under-review       → outlined amber             — customer decision required
//   revision-requested → outlined red               — supplier rework queue
//   accepted           → emerald (active)           — terminal success
//   rejected           → filled red                 — terminal failure
//   closed             → muted neutral (closed)     — terminal, more subdued than draft
//   overdue (derived)  → red   (overdue)            — supplier chase queue
//   late (derived)     → amber (late hue)           — customer review queue (delayed)
const DELIVERABLE_MAP: Record<
  DeliverableDerivedStatus,
  { variant: BadgeVariant; label: string; fullLabel?: string }
> = {
  "not-started": { variant: "draft", label: "Not started" },
  submitted: { variant: "outlinedAmber", label: "Submitted" },
  "under-review": { variant: "outlinedAmber", label: "Under review" },
  "revision-requested": { variant: "outlinedRed", label: "Revision requested" },
  // Conditionally accepted: customer has accepted the deliverable
  // subject to remediation. Outlined amber re-uses the
  // decision-required signal because the cycle is not yet terminal —
  // the conditions still need to clear. The visible label is
  // abbreviated (the full term is too wide for the status column);
  // `fullLabel` carries the precise QITC term as a hover title and
  // the accessible name.
  "conditionally-accepted": {
    variant: "outlinedAmber",
    label: "Cond. accepted",
    fullLabel: "Conditionally accepted",
  },
  // Awaiting resubmission: supplier-action queue after a failed
  // acceptance cycle. Outlined rose signals recoverable failure,
  // distinct from filled red (terminal Rejected). Abbreviated like
  // conditionally-accepted.
  "awaiting-resubmission": {
    variant: "outlinedRose",
    label: "Awaiting resub.",
    fullLabel: "Awaiting resubmission",
  },
  accepted: { variant: "active", label: "Accepted" },
  rejected: { variant: "filledRed", label: "Rejected" },
  closed: { variant: "closed", label: "Closed" },
  overdue: { variant: "overdue", label: "Overdue" },
  late: { variant: "late", label: "Late" },
};

const MILESTONE_MAP: Record<
  MilestoneDisplayStatus,
  { variant: BadgeVariant; label: string }
> = {
  upcoming: { variant: "upcoming", label: "Upcoming" },
  "due-soon": { variant: "dueSoon", label: "Due soon" },
  "partially-completed": {
    variant: "partiallyCompleted",
    label: "Partially completed",
  },
  overdue: { variant: "overdue", label: "Overdue" },
  completed: { variant: "completed", label: "Completed" },
  "completed-late": { variant: "completedLate", label: "Completed late" },
};

const MILESTONE_DERIVED_MAP: Record<
  MilestoneDerivedStatus,
  { variant: BadgeVariant; label: string }
> = {
  upcoming: { variant: "upcoming", label: "Upcoming" },
  delivered: { variant: "delivered", label: "Delivered" },
  "partially-accepted": { variant: "partiallyCompleted", label: "Partially accepted" },
  accepted: { variant: "completed", label: "Accepted" },
  paid: { variant: "completed", label: "Paid" },
};

// Risk overlays ride on top of the lifecycle pill. `late` reuses the existing
// red `badge-overdue` token so the design system gains a new vocabulary
// without a new colour. `delivered-late` (amber) keeps the existing
// `badge-completedLate` palette. `due-soon` reuses the brand-violet pill.
const MILESTONE_OVERLAY_MAP: Record<
  MilestoneRiskOverlayKind,
  { variant: BadgeVariant; label: string }
> = {
  late: { variant: "overdue", label: "Late" },
  "delivered-late": { variant: "completedLate", label: "Delivered late" },
  "due-soon": { variant: "dueSoon", label: "Due soon" },
};

// Amber outline class for "Partially accepted" — applied directly as Tailwind
// utilities so test assertions on className (which don't see @apply expansions)
// can verify the amber+border signal without relying on jsdom CSS processing.
const PARTIAL_ACCEPTED_CLASS =
  "badge border border-amber-500 bg-transparent text-amber-700 dark:text-amber-300";

function renderMilestoneV2(status: MilestoneDisplayStatusV2) {
  const { lifecycle, riskOverlay, secondaryPill } = status;
  // Upcoming + overlay collapses to a single overlay pill — adding "Upcoming"
  // alongside "Late" or "Due soon" is redundant.
  if (lifecycle === "upcoming" && riskOverlay) {
    const overlay = MILESTONE_OVERLAY_MAP[riskOverlay];
    return <span className={BADGE_CLASS[overlay.variant]}>{overlay.label}</span>;
  }
  // Delivered + delivered-late: the overlay already says "delivered"
  // (just late), so showing both pills was redundant. Collapse to the
  // single overlay pill — same pattern as upcoming + late / due-soon.
  if (lifecycle === "delivered" && riskOverlay === "delivered-late") {
    const overlay = MILESTONE_OVERLAY_MAP[riskOverlay];
    return <span className={BADGE_CLASS[overlay.variant]}>{overlay.label}</span>;
  }
  const base = MILESTONE_DERIVED_MAP[lifecycle];
  const baseClass =
    lifecycle === "partially-accepted"
      ? PARTIAL_ACCEPTED_CLASS
      : BADGE_CLASS[base.variant];
  // Partial+paid: render both lifecycle pill and a "Paid" secondary pill so the
  // user sees the full state without collapsing either into the other.
  if (secondaryPill === "paid") {
    const paid = MILESTONE_DERIVED_MAP["paid"];
    return (
      <span
        data-testid="milestone-status-pair"
        className="inline-flex items-center gap-1"
      >
        <span className={baseClass}>{base.label}</span>
        <span className={BADGE_CLASS[paid.variant]}>{paid.label}</span>
      </span>
    );
  }
  if (!riskOverlay) {
    return <span className={baseClass}>{base.label}</span>;
  }
  const overlay = MILESTONE_OVERLAY_MAP[riskOverlay];
  // Pill order is chronological: the earlier event reads first, the later
  // state reads last. `delivered-late` describes the delivery itself, so
  // when the lifecycle has progressed past delivery (partial / accepted /
  // paid) the overlay pill belongs in front of the lifecycle pill —
  // [Delivered late, Accepted] reads as "delivered late, then accepted",
  // matching the [Partially accepted, Paid] ordering of partial+paid.
  // For lifecycle === "delivered" the overlay is an annotation on the
  // same event, so the existing [Delivered, Delivered late] order stays.
  const overlayPrecedesLifecycle =
    riskOverlay === "delivered-late" &&
    (lifecycle === "partially-accepted" ||
      lifecycle === "accepted" ||
      lifecycle === "paid");
  return (
    <span
      data-testid="milestone-status-pair"
      className="inline-flex items-center gap-1"
    >
      {overlayPrecedesLifecycle ? (
        <>
          <span className={BADGE_CLASS[overlay.variant]}>{overlay.label}</span>
          <span className={baseClass}>{base.label}</span>
        </>
      ) : (
        <>
          <span className={baseClass}>{base.label}</span>
          <span className={BADGE_CLASS[overlay.variant]}>{overlay.label}</span>
        </>
      )}
    </span>
  );
}

const RAG_MAP: Record<RagRating, { variant: BadgeVariant; dot: string }> = {
  green: { variant: "active", dot: "bg-emerald-500" },
  amber: { variant: "pending", dot: "bg-amber-500" },
  red: { variant: "expired", dot: "bg-red-500" },
};

// Active and Completed share the emerald family on purpose — both are
// lifecycle-positive states; the pill *label* disambiguates them. See brand
// SKILL.md §9.1 for the rationale (reserves brand violet for genuine
// primary actions and avoids the violet-density rule clash).
const SOW_MAP: Record<SowStatus, { variant: BadgeVariant; label: string }> = {
  draft: { variant: "draft", label: "Draft" },
  active: { variant: "active", label: "Active" },
  completed: { variant: "completed", label: "Completed" },
  cancelled: { variant: "archived", label: "Cancelled" },
};

// Proposed=amber (decision required) per the brand SKILL.md §9.2 reading of
// amber as "awaiting attention" — same precedent as deliverable pending /
// submitted / under-review. Approved=emerald, rejected=red. Both terminal.
const VARIATION_MAP: Record<
  VariationStatus,
  { variant: BadgeVariant; label: string }
> = {
  proposed: { variant: "pending", label: "Proposed" },
  approved: { variant: "active", label: "Approved" },
  rejected: { variant: "expired", label: "Rejected" },
};

// Issue status pills (outlined-vs-filled discipline — brand SKILL.md §9
// "Issue status pills"). Open is the single loud (filled red-600) pill: the
// action signal needing triage. In progress (outlined sky) and Awaiting
// response (outlined amber) are the quieter in-flight states; Awaiting
// re-uses the deliverable submitted/under-review amber — same "ball in
// another party's court" semantic. Resolved (outlined emerald) is positive
// but provisional. Closed re-uses the muted badge-archived (terminal,
// quietest). Three new classes (openIssue / outlinedSky / outlinedEmerald);
// outlinedAmber + archived re-used.
const ISSUE_MAP: Record<
  IssueStatus,
  { variant: BadgeVariant; label: string }
> = {
  open: { variant: "openIssue", label: "Open" },
  in_progress: { variant: "outlinedSky", label: "In progress" },
  awaiting_response: { variant: "outlinedAmber", label: "Awaiting response" },
  resolved: { variant: "outlinedEmerald", label: "Resolved" },
  closed: { variant: "archived", label: "Closed" },
};

export default function TrackingStatusBadge(props: TrackingStatusBadgeProps) {
  switch (props.kind) {
    case "insurance": {
      // Superseded (renewed) rows resolve their attention state:
      //   - expired  → muted slate "Expired" pill so audit-only data doesn't
      //     compete with active expired records that still need attention.
      //   - expiring → "Renewed" pill on the existing `badge-outlinedEmerald`
      //     surface. Reused (not a new token) for "resolved attention,
      //     non-terminal" — filled emerald already means Current/active, so
      //     the outlined variant reads as renewed-but-not-yet-the-live-policy.
      //     The successor name rides as a `title` so the suppressed attention
      //     dot is auditable on hover.
      if (props.superseded && props.status === "expired") {
        return <span className="badge-expiredHistorical">Expired</span>;
      }
      if (props.superseded && props.status === "expiring") {
        // Successor name rides as both a `title` (sighted hover) and the
        // accessible name (`aria-label`) so screen readers announce why this
        // row's attention dot was suppressed — `title` alone is hover-only and
        // not reliably in the accessibility tree.
        const renewedLabel = props.successorName
          ? `Renewed, superseded by ${props.successorName}`
          : undefined;
        return (
          <span
            className={BADGE_CLASS.outlinedEmerald}
            {...(props.successorName
              ? { title: props.successorName, "aria-label": renewedLabel }
              : {})}
          >
            Renewed
          </span>
        );
      }
      const { variant, label } = INSURANCE_MAP[props.status];
      return <span className={BADGE_CLASS[variant]}>{label}</span>;
    }
    case "deliverable": {
      const { variant, label, fullLabel } = DELIVERABLE_MAP[props.status];
      // Abbreviated labels expose the full term on hover and as the
      // accessible name — screen readers should never voice "Cond.".
      return (
        <span
          className={BADGE_CLASS[variant]}
          {...(fullLabel ? { title: fullLabel, "aria-label": fullLabel } : {})}
        >
          {label}
        </span>
      );
    }
    case "milestone": {
      // v2 shape — `{ lifecycle, riskOverlay? }` — renders a lifecycle pill and
      // (when set) an adjacent overlay pill so QITC vocabulary stays distinct.
      // Carve-out: when the lifecycle is `upcoming`, the overlay alone reads
      // correctly ("Late" / "Due soon" already imply pre-delivery), so we drop
      // the lifecycle pill to avoid redundant headlines.
      if (typeof props.status === "object" && props.status !== null) {
        return renderMilestoneV2(props.status);
      }
      if (
        Object.prototype.hasOwnProperty.call(
          MILESTONE_DERIVED_MAP,
          props.status,
        )
      ) {
        const { variant, label } =
          MILESTONE_DERIVED_MAP[props.status as MilestoneDerivedStatus];
        return <span className={BADGE_CLASS[variant]}>{label}</span>;
      }
      const { variant, label } =
        MILESTONE_MAP[props.status as MilestoneDisplayStatus];
      return <span className={BADGE_CLASS[variant]}>{label}</span>;
    }
    case "rag": {
      const { variant, dot } = RAG_MAP[props.rating];
      return (
        <span
          className={clsx(
            BADGE_CLASS[variant],
            "inline-flex items-center gap-1.5",
          )}
        >
          <span
            className={clsx("h-2 w-2 rounded-full", dot)}
            aria-hidden="true"
          />
          <span className="sr-only">RAG status: </span>
          {RAG_LABELS[props.rating]}
        </span>
      );
    }
    case "sow": {
      const { variant, label } = SOW_MAP[props.status];
      return <span className={BADGE_CLASS[variant]}>{label}</span>;
    }
    case "variation": {
      const { variant, label } = VARIATION_MAP[props.status];
      return <span className={BADGE_CLASS[variant]}>{label}</span>;
    }
    case "issue": {
      const { variant, label } = ISSUE_MAP[props.status];
      return <span className={BADGE_CLASS[variant]}>{label}</span>;
    }
  }
}
